import React from "react";
import { Filter, RotateCcw } from "lucide-react";
import Button from "../ui/Button";
import { DateField, SelectField, TextField } from "../ui/Field";
import { OFFICE_NAMES, officeLabel } from "../../lib/format";

/**
 * Filters that narrow the rows already on screen.
 *
 * These are deliberately separate from the search form. The search form asks
 * the register a question and costs an API call; this bar reshapes the answer
 * and costs nothing. Keeping them apart means a user can slice 100 results
 * eight different ways without burning through the rate limit.
 *
 * Everything filters live as you type. The row count in the results header
 * updates with it, so there's no "apply" button to forget to press.
 */
export default function TableFilterBar({
  filters,
  onChange,
  onReset,
  options,
  activeCount,
  hasSimilarity,
}) {
  const set = (key) => (event) =>
    onChange({ ...filters, [key]: event.target.value });

  const jurisdictionOptions = [
    { value: "", label: "Any jurisdiction" },
    // Every country/office, not just the ones present in the current rows,
    // plus any unknown code that appears in the results.
    ...[
      ...new Set([
        ...Object.keys(OFFICE_NAMES).filter((code) => code !== "EU"),
        ...options.jurisdictions,
      ]),
    ]
      .sort((a, b) =>
        (OFFICE_NAMES[a] || a).localeCompare(OFFICE_NAMES[b] || b),
      )
      .map((code) => ({
        value: code,
        label: officeLabel(code),
      })),
  ];

  const statusOptions = [
    { value: "", label: "Any status" },
    ...options.statuses.map((status) => ({ value: status, label: status })),
  ];

  const niceOptions = [
    { value: "", label: "Any class" },
    ...options.niceClasses.map((c) => ({ value: c, label: `Class ${c}` })),
  ];

  const similarityOptions = [
    { value: "", label: "Any score" },
    { value: "40", label: "40% and above" },
    { value: "60", label: "60% and above" },
    { value: "75", label: "75% and above" },
  ];

  return (
    <section
      aria-label="Filter these results"
      className="rounded-t-soft border border-b-0 border-ink-200 bg-white px-4 py-4 sm:px-5"
    >
      <div className="mb-3.5 flex items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 text-sm font-semibold text-ink-800">
          <Filter className="h-4 w-4 text-ink-500" aria-hidden="true" />
          Filter these results
          {activeCount > 0 && (
            <span className="rounded bg-[#8f5eeb]-100 px-1.5 py-0.5 text-micro font-semibold text-[#8f5eeb]-600">
              {activeCount} active
            </span>
          )}
        </h2>

        <Button
          variant="ghost"
          size="sm"
          icon={RotateCcw}
          onClick={onReset}
          disabled={activeCount === 0}
        >
          Clear filters
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-8">
        <TextField
          label="Trade mark name"
          value={filters.wordMark}
          onChange={set("wordMark")}
          placeholder="Contains…"
          className="xl:col-span-2"
        />
        <TextField
          label="Application number"
          value={filters.applicationNumber}
          onChange={set("applicationNumber")}
          placeholder="Contains…"
        />
        <TextField
          label="Applicant"
          value={filters.applicant}
          onChange={set("applicant")}
          placeholder="Contains…"
          className="xl:col-span-2"
        />
        <SelectField
          label="Jurisdiction"
          value={filters.jurisdiction}
          onChange={set("jurisdiction")}
          options={jurisdictionOptions}
        />
        <SelectField
          label="Status"
          value={filters.status}
          onChange={set("status")}
          options={statusOptions}
        />
        <SelectField
          label="Nice class"
          value={filters.niceClass}
          onChange={set("niceClass")}
          options={niceOptions}
        />
        <DateField
          label="Filed from"
          value={filters.filedFrom}
          onChange={set("filedFrom")}
        />
        <DateField
          label="Filed to"
          value={filters.filedTo}
          onChange={set("filedTo")}
        />
        {hasSimilarity && (
          <SelectField
            label="AI similarity"
            value={filters.minSimilarity}
            onChange={set("minSimilarity")}
            options={similarityOptions}
          />
        )}
      </div>
    </section>
  );
}
